import React, { useState, useMemo, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import CreateOverride from './CreateOverride'
import { useFileUpload } from '../hooks/useFileUpload'
import { useOverrideManagement } from '../hooks/useOverrideManagement'
import { SettingOutlined, TableOutlined, CheckCircleOutlined } from '@ant-design/icons'
import useStepper from '../hooks/useStepper'
import { useValidationConfig } from '../hooks/useValidationConfig'
import { logFormattedMessage } from '../../../common/utils/consoleLogging'

const OVERRIDE_STEPS = [
  {
    title: 'Configuration',
    description: 'Set up override details',
    icon: SettingOutlined,
  },
  {
    title: 'Data Review',
    description: 'Review and modify data',
    icon: TableOutlined,
  },
  {
    title: 'Summary',
    description: 'Override created successfully',
    icon: CheckCircleOutlined,
  },
]

const CreateOverrideManager = () => {
  const { type } = useParams()
  const [fileHeaders, setFileHeaders] = useState(new Set())
  const [gridData, setGridData] = useState(null)
  const [validationSummary, setValidationSummary] = useState(null)
  const { currentStep, nextStep, previousStep, goToStep } = useStepper(OVERRIDE_STEPS) // eslint-disable-line no-unused-vars
  const [overrideName, setOverrideName] = useState('')
  const [fileName, setFileName] = useState('')
  const [validationErrors, setValidationErrors] = useState([])
  const [fieldsToOverride, setFieldsToOverride] = useState([])
  const [hasCompletedConfiguration, setHasCompletedConfiguration] = useState(false)
  const [hasCompletedDataReview, setHasCompletedDataReview] = useState(false)
  const [isLoading, setIsLoading] = useState(false) // eslint-disable-line no-unused-vars

  // Track the last valid configuration state
  const [lastValidConfig, setLastValidConfig] = useState(null)

  // Check if current configuration matches the last valid state
  const isConfigurationValid = useMemo(() => {
    if (!hasCompletedConfiguration || !lastValidConfig) {
      return false
    }

    return (
      lastValidConfig.overrideName === overrideName &&
      lastValidConfig.fileName === fileName &&
      JSON.stringify(lastValidConfig.fieldsToOverride) === JSON.stringify(fieldsToOverride)
    )
  }, [hasCompletedConfiguration, lastValidConfig, overrideName, fileName, fieldsToOverride])

  // Track completed steps
  const completedSteps = useMemo(
    () => ({
      0: hasCompletedConfiguration,
      1: hasCompletedDataReview && isConfigurationValid,
      2: false, // Summary step is never "completed" as it's the final step
    }),
    [hasCompletedConfiguration, hasCompletedDataReview, isConfigurationValid],
  )

  const validationConfig = useValidationConfig(fileHeaders)
  const { activeSection, sectionTitle, handleBack } = useOverrideManagement()

  // Move useFileUpload to top level
  const { handleFileUpload } = useFileUpload({
    activeSection: type || activeSection,
    onDataProcessed: (data, summary) => {
      setGridData(data)
      setValidationSummary(summary)
    },
    setFileHeaders,
  })

  const handleFileUploadWrapper = useCallback(
    (rows, metadata, formValues) => {
      try {
        logFormattedMessage('handleFileUploadWrapper start', {
          formValues,
        })

        // Save current form values
        const currentValues = {
          overrideName: formValues.overrideName,
          fieldsToOverride: formValues.fieldsToOverride,
        }

        setFileName(metadata.fileName)
        const result = handleFileUpload(rows, metadata, formValues.fieldsToOverride)

        if (result.validationErrors) {
          setValidationErrors(result.validationErrors)
        }

        // Restore form values
        setOverrideName(currentValues.overrideName)
        setFieldsToOverride(currentValues.fieldsToOverride)

        logFormattedMessage('handleFileUploadWrapper end', {
          formValues: currentValues,
        })
      } catch (error) {
        setValidationErrors(['Error processing file'])
      }
    },
    [handleFileUpload],
  )

  const handleConfigurationSubmit = useCallback(
    (values) => {
      setOverrideName(values.overrideName)
      setFieldsToOverride(values.fieldsToOverride)
      setHasCompletedConfiguration(true)
      // Store the valid configuration state
      setLastValidConfig({
        overrideName: values.overrideName,
        fieldsToOverride: values.fieldsToOverride,
        fileName,
      })
      nextStep()
    },
    [nextStep, fileName],
  )

  const overrideProps = useMemo(
    () => ({
      title: sectionTitle,
      type: type || activeSection,
      onBack: handleBack,
    }),
    [sectionTitle, type, activeSection, handleBack],
  )

  const fileHandlingProps = useMemo(
    () => ({
      onFileUpload: handleFileUploadWrapper,
      fileName,
      validationErrors,
      validationSummary,
      validationConfig,
      isLoading,
    }),
    [handleFileUploadWrapper, fileName, validationErrors, validationSummary, validationConfig, isLoading],
  )

  const handleSave = useCallback(
    async (data) => {
      try {
        // TODO: Implement save
        setHasCompletedDataReview(true)
        nextStep()
      } catch (error) {
        logFormattedMessage('Error saving', { error })
      }
    },
    [nextStep],
  )

  // Memoize all props to prevent unnecessary re-renders
  const formProps = useMemo(
    () => ({
      handleConfigurationSubmit,
      hasCompletedConfiguration,
      overrideName,
      fieldsToOverride,
    }),
    [handleConfigurationSubmit, hasCompletedConfiguration, overrideName, fieldsToOverride],
  )

  const stepperProps = useMemo(
    () => ({
      currentStep,
      steps: OVERRIDE_STEPS,
      goToStep: (step) => {
        // Only allow going to completed steps or the current step
        if (completedSteps[step] || step === currentStep) {
          goToStep(step)
        }
      },
      completedSteps,
    }),
    [currentStep, goToStep, completedSteps],
  )

  const gridProps = useMemo(
    () => ({
      gridData,
      onSave: handleSave,
    }),
    [gridData, handleSave],
  )

  return (
    <CreateOverride
      overrideProps={overrideProps}
      fileHandlingProps={fileHandlingProps}
      formProps={formProps}
      stepperProps={stepperProps}
      gridProps={gridProps}
    />
  )
}

export default CreateOverrideManager
